import { createContext, useContext, useEffect, useRef } from 'react';
import type { AllowlistRole as Role } from '@bbbk/schema';
import { lookaheadWindows } from '../components/today/needsAttention';

export interface DateWindow {
  from: string;
  to: string;
}

// Cross-section navigation for the keep-alive shell. A deeply-nested control
// (e.g. a booking row in the owner modal) can switch the active section and
// hand the target page a one-shot intent — "open this booking" — which the page
// consumes on arrival. Kept tiny: the only intent so far is a booking id.
export interface NavApi {
  // Switch the active section; optionally carry a one-shot intent — a booking to
  // open, a status filter to apply, and/or a day to land on — for the target
  // page to consume.
  go: (view: string, opts?: { bookingId?: string; status?: string; date?: string; window?: DateWindow }) => void;
  // The booking the bookings page should open on arrival (null when none).
  bookingIntent: string | null;
  // Clear the intent once the page has acted on it.
  consumeBookingIntent: () => void;
  // A status filter the bookings page should apply on arrival (e.g. 'arrived'
  // from the Today "In residence → view all"). Null when none.
  statusIntent: string | null;
  consumeStatusIntent: () => void;
  // A day to land on, SCOPED to the destination view, so only that page
  // consumes it (every kept-alive page sees the intent at once — the `view`
  // guard stops Daily/Bookings/Rota fighting over a single shared day). E.g.
  // "On-duty → view all" sends { view: 'schedule', date } so only Rota acts.
  dateIntent: { view: string; date: string } | null;
  consumeDateIntent: () => void;
  // A date WINDOW to land on, scoped to the destination view — Daily's "Open
  // in Bookings" hands the Bookings page the seven days its Needs attention
  // panel read (viewings-in-the-bookings-list AC11). The page REPLACES its
  // filter with it rather than merging, so a stale filter cannot empty the
  // list it lands on.
  windowIntent: { view: string; window: DateWindow } | null;
  consumeWindowIntent: () => void;
  // The ONE shared anchor date behind the global top-bar date picker
  // (app-shell-redesign AC4). Every date-scoped page (Daily, Messages, Rota,
  // Finances) reads it at its own granularity — a day, a month, a year — and
  // steps it through the bar, not a per-page control. There is exactly one date
  // in the app now, so switching pages keeps the anchor and only reinterprets it.
  anchor: Date;
  setAnchor: (d: Date) => void;
  // The signed-in user's role, threaded from the shell so a page can gate a
  // control the same way the nav gates a section. The Daily page's Money chart
  // uses it: Finances is admin+owner only (routeMap NAV), and the desk is worked
  // by helpers, so the option must not appear for them. The API is the real
  // gate — /api/finances/* 403s a helper — this only stops offering a door that
  // is locked.
  role: Role | null;
}

const NavContext = createContext<NavApi>({
  go: () => {},
  bookingIntent: null,
  consumeBookingIntent: () => {},
  statusIntent: null,
  consumeStatusIntent: () => {},
  dateIntent: null,
  consumeDateIntent: () => {},
  windowIntent: null,
  consumeWindowIntent: () => {},
  // A fixed fallback so a page rendered outside the provider (unit render tests)
  // still resolves a date; the live value always comes from AppShell.
  anchor: new Date(2026, 0, 1),
  setAnchor: () => {},
  // No role until the shell provides one — a page rendered outside the provider
  // (unit render tests) must not accidentally read as privileged.
  role: null,
});

export const NavProvider = NavContext.Provider;

export function useNav(): NavApi {
  return useContext(NavContext);
}

// Consume a view-scoped date intent: when another page jumps here on a day
// (e.g. a stat-card "view all"), run `onDate` with it once, then clear it. The
// `view` guard means only the destination page reacts — every kept-alive page
// sees the intent, so without it Daily/Bookings/Rota would fight over one day.
// onDate is read through a ref so the effect stays keyed on the intent alone.
export function useDateIntent(view: string, onDate: (date: string) => void): void {
  const { dateIntent, consumeDateIntent } = useNav();
  const cb = useRef(onDate);
  cb.current = onDate;
  useEffect(() => {
    if (dateIntent?.view !== view) return;
    cb.current(dateIntent.date);
    consumeDateIntent();
  }, [dateIntent, consumeDateIntent, view]);
}

// Where the day-stat "view all" links jump, carrying the day in view — one home
// so Daily and Messages send the same intent and a route-key change lands once.
export const dayStatNav = {
  onDuty: (nav: NavApi, date: string) => nav.go('schedule', { date }),
  inResidence: (nav: NavApi, date: string) => nav.go('bookings', { date }),
  arriving: (nav: NavApi, date: string) => nav.go('today', { date }),
};

// Consume a view-scoped window intent, once, on arrival — the same shape as
// useDateIntent, for the same reason (every kept-alive page sees every intent).
export function useWindowIntent(view: string, onWindow: (window: DateWindow) => void): void {
  const { windowIntent, consumeWindowIntent } = useNav();
  const cb = useRef(onWindow);
  cb.current = onWindow;
  useEffect(() => {
    if (windowIntent?.view !== view) return;
    cb.current(windowIntent.window);
    consumeWindowIntent();
  }, [windowIntent, consumeWindowIntent, view]);
}

// Where Daily's Needs attention "Open in Bookings" jumps: the Bookings page at
// All, with the window the panel read — computed from Daily's anchor AT THE
// CLICK and passed whole. The Bookings page never derives a window itself, so
// the app's shared anchor can never leak into it (architecture ADR-5).
export const attentionNav = {
  openInBookings: (nav: NavApi, anchor: string) => nav.go('bookings', { window: lookaheadWindows(anchor).range }),
};
